import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, openVideoPlayer, trackEvent } from '../../../../../../../utils/appBridge'
import { PlansScreen, PlansTimerEventIds } from '../../../../analytics/plansEvents'
import { isPlanDropDelayed, usePlanCountdown } from '../shared/usePlanCountdown'
import { REBALANCE_FEATURE_ROWS } from '../shared/rebalanceFeatureRows'
import {
  TimerPanelCountdownSection,
  TimerPanelFeatureRows,
  TimerPanelFooterCopy,
  TimerPanelFooterHeading,
  TimerPanelShell
} from '../shared/TimerPanelShell'

import { getPlansFlowTheme, PlansThemeFlow } from '../../header/common/riskProfileHeaderTheme'
import { rebalanceTimerCountdownChrome } from '../shared/timerCountdownChrome'

const FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.REBALANCE)
const VIDEO_URL =
  'https://pumobileweb.uni.club/power-academy/video-player?videoId=2ddf282438344ae2b80bae383bb44220&moduleId=MD2943832c786c493ea40e4ed1d82f4abf&courseId=COURSEde919cce87264b1480e91195a96442b0&prevStatusBarColor=20A5DE'

type RebalanceTimerPanelProps = {
  timeToPlanDrop?: number | null
  isSubscriptionEligible: boolean
}

/** Full-tab shell before PLAN_ROLLED_OUT. Mounted by RebalancePageContent. */
export function RebalanceTimerPanel({
  timeToPlanDrop,
  isSubscriptionEligible
}: RebalanceTimerPanelProps) {
  const isDelayed = isPlanDropDelayed(timeToPlanDrop)
  const countdownParts = usePlanCountdown(isDelayed ? 0 : timeToPlanDrop)

  return (
    <TimerPanelShell
      background={FLOW_THEME.chromeBackground}
      title="Rebalance Plan"
      description="Know which funds to exit and switch to better-performing ones, tax-efficiently."
      hero={
        <TimerPanelCountdownSection
          {...rebalanceTimerCountdownChrome(FLOW_THEME, {
            readyIn: 'Your plan will be ready in',
            delayTitle: 'Your rebalance plan is delayed',
            delayBody:
              "We need extra time to prepare your Rebalance plan. We'll update you as soon as it's ready.",
            unlockPrompt: 'Reserve your spot today with Elite'
          })}
          countdownParts={countdownParts}
          isDelayed={isDelayed}
          isSubscriptionEligible={isSubscriptionEligible}
          unlockButtonLabel="Reserve with Elite"
          analyticsScreen={PlansScreen.LumpsumRebalanceTimer}
        />
      }
      footer={
        <>
          <TimerPanelFooterHeading>How does rebalancing help?</TimerPanelFooterHeading>
          <div
            className="relative mb-12 cursor-pointer"
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumRebalanceTimer,
                  eventId: PlansTimerEventIds.ExplainerVideo,
                  buttonLabel: 'Explainer video'
                }
              })
              openVideoPlayer(VIDEO_URL)
            }}
          >
            <PlayIconOverlay />
            <img className="w-full rounded-xl" src="/images/rebalancing-plan.png" alt="" />
          </div>
          <TimerPanelFooterHeading>Keep your portfolio in top form</TimerPanelFooterHeading>
          <TimerPanelFooterCopy>
            Every month, we assess your portfolio to see if any smart switches can be made.
            Here&apos;s what we focus on:
          </TimerPanelFooterCopy>
          <TimerPanelFeatureRows rows={REBALANCE_FEATURE_ROWS} iconColor={FLOW_THEME.primary} />
        </>
      }
    />
  )
}

export function PlayIconOverlay() {
  return (
    <svg
      aria-hidden
      className="absolute top-1/2 left-1/2 z-[1] -translate-x-1/2 -translate-y-1/2"
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
    >
      <circle cx="20" cy="20" r="20" fill="#D6DCE1" />
      <path
        d="M17 26.9991V14.0723C17 13.2353 17.967 12.7686 18.6222 13.2895L26.9866 19.9381C27.4992 20.3456 27.4884 21.1276 26.9647 21.5207L18.6003 27.7988C17.9411 28.2936 17 27.8233 17 26.9991Z"
        fill="white"
      />
    </svg>
  )
}
